import {afterNextRender, Inject, Injectable, PLATFORM_ID, RendererFactory2} from '@angular/core'
import {BehaviorSubject, Observable} from 'rxjs'
import {ConfigService} from '../config/config.service'
import {GoogleMapsApi, GoogleMapsInstance} from './google-maps.types'
import {DOCUMENT, isPlatformBrowser} from '@angular/common'
import {filter} from 'rxjs/operators'

@Injectable({
  providedIn: 'root',
})
export class GoogleMapsService {
  private readonly _googleMapsApi$ = new BehaviorSubject<GoogleMapsApi>({
    isLoaded: false,
  })

  public readonly apiLoaded$ = this._googleMapsApi$.pipe(filter(api => !!api?.isLoaded))

  constructor(
    private readonly configService: ConfigService,
    rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) readonly platformId: Record<string, unknown>,
  ) {
    if (isPlatformBrowser(platformId)) {
      const renderer = rendererFactory.createRenderer(null, null)
      const script: HTMLScriptElement = renderer.createElement('script')
      script.type = 'text/javascript'
      script.textContent = `(g=>{var h,a,k,p="The Google Maps JavaScript API",c="google",l="importLibrary",q="__ib__",m=document,b=window;b=b[c]||(b[c]={});var d=b.maps||(b.maps={}),r=new Set,e=new URLSearchParams,u=()=>h||(h=new Promise(async(f,n)=>{await (a=m.createElement("script"));e.set("libraries",[...r]+"");for(k in g)e.set(k.replace(/[A-Z]/g,t=>"_"+t[0].toLowerCase()),g[k]);e.set("callback",c+".maps."+q);a.src=\`https://maps.\${c}apis.com/maps/api/js?\`+e;d[q]=f;a.onerror=()=>h=n(Error(p+" could not load."));a.nonce=m.querySelector("script[nonce]")?.nonce||"";m.head.append(a)}));d[l]?console.warn(p+" only loads once. Ignoring:",g):d[l]=(f,...n)=>r.add(f)&&u().then(()=>d[l](f,...n))})({
    key: "${this.configService.config.googleMaps.apiKey}",
    v: "weekly",
    libraries: "places"
  });`
      renderer.appendChild(this.document.body, script)
    }
    // only run this on client
    afterNextRender({
      write: async () => {
        if (this._googleMapsApi$.value.isLoaded) {
          return
        }
        const [maps, places, core, marker, clusterer] = await Promise.all([
          google.maps.importLibrary('maps'),
          google.maps.importLibrary('places'),
          google.maps.importLibrary('core'),
          google.maps.importLibrary('marker'),
          await import('@googlemaps/markerclustererplus'),
        ])
        const googleMapsApis: GoogleMapsInstance = {
          maps: maps as google.maps.MapsLibrary,
          places: places as google.maps.PlacesLibrary,
          core: core as google.maps.CoreLibrary,
          marker: marker as google.maps.MarkerLibrary,
          clusterer,
        }
        globalThis.MarkerClusterer = clusterer.default
        this._googleMapsApi$.next({isLoaded: true, api: googleMapsApis})
      },
    })
  }

  googleMapsApi$(): Observable<GoogleMapsApi> {
    return this._googleMapsApi$.asObservable()
  }

  parseAddressComponents(components: google.maps.GeocoderAddressComponent[]): AddressComponents {
    const locality = components.find(c => c.types.includes('locality'))?.long_name
    const postalCode = components.find(c => c.types.includes('postal_code'))?.long_name
    const street = components.find(c => c.types.includes('route'))?.long_name
    const houseNumber = components.find(c => c.types.includes('street_number'))?.long_name

    return {
      locality,
      postalCode: postalCode ? +postalCode : undefined,
      streetAndHouseNumber: street ? `${street} ${houseNumber || ''}`.trim() : undefined,
    }
  }
}

export interface AddressComponents {
  locality?: string
  postalCode?: number
  streetAndHouseNumber?: string
}
